// Single source of truth for the pricing A/B test.
//
// FOUR DIFFERENT questions live here. Do not merge them:
//
//   1. "Which pricing does this VIEWER see?"        → pricingSuffix()
//   2. "Does this VIEWER see modern pricing?"       → isModernPricing()
//   3. "Does this VIEWER see v2 pricing exactly?"   → showV2Pricing()
//   4. "Is this PACKAGE on modern pricing?"         → isModernPackage() / modernPackageTagFilter()
//
// (1)-(3) read a user's cohort. (4) reads a package row's Tag and knows nothing about who is
// looking at it. Collapsing them is a bug: a control-cohort user can still HOLD a v2
// subscription they bought earlier, and the credit-expiry/recharge rules must follow the
// package, not the viewer.
//
// (2) vs (3) is the easy one to get wrong, because today they return the same answer for every
// user that exists (no v3 cohort is assigned anywhere yet). They diverge the moment one is:
//
//   isModernPricing() — STRUCTURAL gates. The feature does not exist on modern pricing AT ALL:
//                       annual plans, gifting, the monthly/yearly toggle. A v3 viewer must be
//                       gated out of these exactly like a v2 viewer is.
//   showV2Pricing()   — v2-SPECIFIC copy and layout. Strings that hardcode v2 prices ("$12",
//                       "Want 5 for $10?") or pick a hardcoded v2 package list. A v3 viewer
//                       must NOT be shown these, because v3's prices differ.
//
// Rule of thumb: if the branch says "this feature is unavailable", use isModernPricing(). If it
// says "show this price/number/plan list", use showV2Pricing().

// Every non-legacy pricing generation, oldest first. Adding a generation means adding its
// suffix here and nothing else — every derived helper below reads this list.
export const MODERN_PRICING_SUFFIXES = ["-v2", "-v3"] as const

export type PricingSuffix = "" | (typeof MODERN_PRICING_SUFFIXES)[number]

export type CohortUser = { PricingCohort?: string | null } | null | undefined

// Callers pass `context.user`, which this app types as `unknown` (see the AppLoadContext
// augmentation in entry.server.tsx — it does not survive into route modules). Taking `unknown`
// here and narrowing once is what lets every call site drop its `as any` cast: the resolver is
// the only place that has to cope with the missing type. Pass a user, not a package.
export function pricingCohortOf(user: unknown): string | null {
  if (user && typeof user === "object" && "PricingCohort" in user) {
    return (user as { PricingCohort?: string | null }).PricingCohort ?? null
  }
  return null
}

// The tag suffix the packages this viewer should be offered carry ("" means legacy tags).
//
// The branch order is deliberate and load-bearing: decide on user PRESENCE first, then on
// cohort. Anonymous visitors and null-cohort users deliberately land on OPPOSITE sides:
//
//   viewer                         | pricing
//   -------------------------------|--------
//   anonymous (no user at all)     | v2      ← show v2 wherever v2 packages exist
//   logged in, cohort "test"       | v2
//   logged in, cohort "v3"         | v3      ← wired, unreachable: no v3 cohort is assigned yet
//   logged in, cohort "control"    | legacy
//   logged in, cohort null         | legacy  ← existing/unconfirmed user retains legacy
//
// The null-cohort rule comes from _auth+/confirm.tsx: a null cohort means an
// existing/unconfirmed user who must keep legacy pricing. That is why we must NOT collapse
// this to `user?.PricingCohort === "test"` — that expression would lump anonymous visitors
// in with the control/null cohorts and wrongly show them legacy pricing.
export function pricingSuffix(user: CohortUser | unknown): PricingSuffix {
  // No user at all → anonymous visitor → v2.
  if (!user) return "-v2"
  // Logged in → cohort decides; "control" and null fall through to legacy.
  const cohort = pricingCohortOf(user)
  if (cohort === "test") return "-v2"
  if (cohort === "v3") return "-v3"
  return ""
}

// Build the tag a given viewer's package carries, e.g. tagFor("hello", user) → "hello-v2".
// Annual plans append their own marker AFTER the pricing suffix ("quickie-v2-annual"), so
// pass the suffixed base through: `${tagFor("quickie", user)}-annual`.
export function tagFor(base: string, user: CohortUser | unknown): string {
  return `${base}${pricingSuffix(user)}`
}

// Every tag variant of a base across ALL pricing generations, legacy first.
// Use this for cohort-SPANNING lookups — e.g. "has this human ever bought the welcome
// bundle?", which must match whichever generation they bought it under.
export function allTagVariants(base: string): string[] {
  return [base, ...MODERN_PRICING_SUFFIXES.map((suffix) => `${base}${suffix}`)]
}

// Does this viewer see modern (non-legacy) pricing, of ANY generation? Use this for
// STRUCTURAL gates — features that don't exist on modern pricing at all (annual plans,
// gifting). Use showV2Pricing() only for copy that hardcodes v2-specific prices.
export function isModernPricing(user: CohortUser | unknown): boolean {
  return pricingSuffix(user) !== ""
}

// Does this viewer see v2 pricing SPECIFICALLY? Thin wrapper over pricingSuffix for the call
// sites that pick v2-specific copy or a hardcoded v2 package list. Deliberately false for a v3
// viewer: copy that hardcodes v2 prices must not be shown to them.
//
// If you are about to write `showV2Pricing(user) ? <modern> : <legacy>`, stop: that sends a v3
// viewer down the LEGACY branch. For feature-absence gates you want isModernPricing().
export function showV2Pricing(user: CohortUser | unknown): boolean {
  return pricingSuffix(user) === "-v2"
}

// Is this PACKAGE on modern (non-legacy) pricing? Drives credit banking, the recharge
// transition, and cancellation credit expiry.
//
// NOTE: this is `contains`, not `endsWith`. Annual plans carry tags like "quickie-v2-annual",
// where the pricing marker is in the MIDDLE. It also matches the `contains` filter Prisma
// runs in modernPackageTagFilter(), so the JS and SQL answers can never disagree.
export function isModernPackage(tag: string | null | undefined): boolean {
  if (!tag) return false
  return MODERN_PRICING_SUFFIXES.some((suffix) => tag.includes(suffix))
}

// isModernPackage as a Prisma filter, for the same question asked in the database.
// Use as a Package relation filter: `Package: modernPackageTagFilter()`.
export function modernPackageTagFilter() {
  return { OR: MODERN_PRICING_SUFFIXES.map((suffix) => ({ Tag: { contains: suffix } })) }
}
